<script lang="ts">
export default {
  name: 'TableauModal',
  components: {
    AppSelect,
  },
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { tableauService, accountService } from '@/services/api';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { SelectOption } from '@/types/components/Select';
import Combobox from '@/components/v2/Combobox.vue';
import { X } from 'lucide-vue-next';
import { z } from 'zod';
import AppSelect from '@/components/v2/AppSelect.vue';

// ------
// props
// ------
const props = defineProps<{
  show: boolean;
  mode: 'upload' | 'edit';
  dashboard?: TableauDashboard;
  tableauDomain: string;
  enableAccountSelect?: boolean;
  tableauTypes?: TableauType[];
  accountId?: number;
}>();

// ------
// emits
// ------
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
}>();

// ---------
// computed
// ---------
const tableauDomain = computed(() => props.tableauDomain); // use computed prop here, or this can't be access in dashboardSchema

const selectedAccounts = computed<SelectOption[]>(() => {
  return accounts.value.filter((a) => a.selected);
});

const selectableTableauTypes = computed(() => {
  return props.tableauTypes?.map((type) => {
    return {
      label: type.name,
      value: type.id,
    };
  });
});

// -----
// refs
// -----
const showModal = ref<boolean>(false);
const dashboardName = ref<string>('');
const dashboardUrl = ref<string>('');
const dashboardTypeId = ref<number>();
const nameError = ref<string>('');
const urlError = ref<string>('');
const typeError = ref<string>('');
const accounts = ref<SelectOption[]>([]);
const selectedAccount = ref('');

const dashboardSchema = z.object({
  name: z.string().nonempty('A name is required').max(150, 'Name cannot exceed 150 characters'),
  url: z
    .string()
    .nonempty('A URL is required')
    .url('Invalid URL format')
    .refine((data) => data.startsWith('https://'), {
      message: "URL must start with 'https://'",
    })
    .refine(
      (data) => {
        try {
          const urlObj = new URL(data);
          return urlObj.hostname.includes(tableauDomain.value);
        } catch (e) {
          return false;
        }
      },
      {
        message: `URL's domain must be ${tableauDomain.value}`,
      },
    ),
});

// ---------
// watchers
// ---------
watch(
  () => props.show,
  (newValue) => {
    showModal.value = newValue;
    if (props.mode === 'edit' && props.dashboard) {
      dashboardName.value = props.dashboard.name;
      dashboardUrl.value = props.dashboard.dashboardUrl;
      dashboardTypeId.value = props.dashboard.tableauTypeId;
      selectedAccount.value = props.dashboard?.accountId?.toString() || '';
    } else {
      dashboardName.value = '';
      dashboardUrl.value = '';
    }
  },
);

// --------
// methods
// --------
async function handleSave() {
  validateName();
  validateUrl();
  validateType();

  if (nameError.value || urlError.value || !dashboardTypeId.value) {
    return;
  }

  try {
    let accountsList: number[] = [];
    if (props.enableAccountSelect) {
      accountsList = selectedAccounts.value.map((a) => parseInt(a.value));
    } else {
      accountsList = props.accountId ? [props.accountId] : [];
    }
    if (props.mode === 'edit' && props.dashboard) {
      await tableauService.saveDashboard(
        dashboardName.value,
        dashboardUrl.value,
        dashboardTypeId.value,
        props.dashboard.id,
        accountsList,
      );
    } else {
      await tableauService.saveDashboard(
        dashboardName.value,
        dashboardUrl.value,
        dashboardTypeId.value,
        0,
        accountsList,
      );
    }
    clearDataFields();
    deselectAccounts();
    showModal.value = false;
    emit('save');
    emit('close');
  } catch (error) {
    console.error(error);
  }
}

function handleClose() {
  clearDataFields();
  deselectAccounts();
  showModal.value = false;
  emit('close');
}

function clearDataFields() {
  dashboardName.value = '';
  dashboardUrl.value = '';
  dashboardTypeId.value = 0;
  nameError.value = '';
  urlError.value = '';
  selectedAccount.value = '';
}

function validateName() {
  const result = dashboardSchema.safeParse({
    name: dashboardName.value,
  });

  if (result.success) {
    nameError.value = '';
  } else {
    nameError.value = result.error?.errors.find((e) => e.path[0] === 'name')?.message || '';
  }
}

function validateUrl() {
  const result = dashboardSchema.safeParse({
    url: dashboardUrl.value,
  });

  if (result.success) {
    urlError.value = '';
  } else {
    urlError.value = result.error?.errors.find((e) => e.path[0] === 'url')?.message || '';
  }
}

function validateType() {
  if (!dashboardTypeId.value) {
    typeError.value = 'A type is required';
  } else {
    typeError.value = '';
  }
}

//// Account Select
function handleAccountSelect(account: SelectOption) {
  accounts.value = accounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: true };
    }
    return a;
  });
  // Clear input for rapid multi-select
  selectedAccount.value = '';
}
function handleAccountRemove(account: SelectOption) {
  accounts.value = accounts.value.map((a) => {
    if (a.value === account.value) {
      return { ...a, selected: false };
    }
    return a;
  });
}
function deselectAccounts() {
  accounts.value = accounts.value.map((a) => {
    return { ...a, selected: false };
  });
}

// -----------
// lifecycles
// -----------
onMounted(async () => {
  showModal.value = props.show;

  if (props.enableAccountSelect) {
    const accountList = await accountService.list();
    accounts.value = accountList.map((account) => {
      return {
        label: account.accountName,
        value: account.accountID.toString(),
      };
    });
  }
});
</script>

<template>
  <dialog class="tw:modal" :class="showModal && 'tw:modal-open'">
    <div class="tw:form-control tw:modal-box tw:flex tw:flex-col tw:gap-4 tw:overflow-visible">
      <h2 class="tw:pb-3">{{ props.mode === 'edit' ? 'Edit' : 'Upload' }} Dashboard</h2>
      <div class="tw:flex tw:flex-col tw:gap-6">
        <div>
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">Name</span>
          </label>
          <input
            type="text"
            placeholder=""
            class="tw:input tw:input-md tw:w-full"
            v-model="dashboardName"
            @blur="validateName" />
          <span class="tw:text-red-500">{{ nameError }}</span>
        </div>
        <div>
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">URL</span>
          </label>
          <input
            type="text"
            placeholder=""
            class="tw:input tw:input-md tw:w-full"
            v-model="dashboardUrl"
            @blur="validateUrl" />
          <span class="tw:text-red-500">{{ urlError }}</span>
        </div>
        <div>
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">Type</span>
          </label>
          <AppSelect
            v-model="dashboardTypeId"
            :options="selectableTableauTypes"
            class="tw:h-9 tw:w-full"
            placeholder="Select type"
            @update:modelValue="validateType" />
          <span class="tw:text-red-500">{{ typeError }}</span>
        </div>
      </div>

      <!-- ACCOUNTS-->
      <div v-if="props.enableAccountSelect" class="tw:w-full">
        <div class="tw:flex tw:flex-col">
          <label class="tw:label">
            <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1">Accounts</span>
          </label>

          <div v-if="selectedAccounts.length > 0" class="tw:flex tw:flex-wrap tw:pb-2">
            <div
              v-for="account in selectedAccounts"
              :key="account.value"
              @click="handleAccountRemove(account)"
              class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
              <div class="tw:p-1">{{ account.label }}</div>
              <X :size="12" class="tw:group-hover:stroke-red-600" />
            </div>
          </div>

          <Combobox
            v-model="selectedAccount"
            :options="accounts"
            :placeholder="'Select Accounts'"
            @selected="handleAccountSelect" />
        </div>
      </div>

      <div class="tw:flex tw:gap-4"></div>
      <div class="tw:modal-action">
        <button class="tw:btn tw:btn-sm" @click="handleClose">Cancel</button>
        <button class="tw:btn tw:btn-sm tw:btn-primary" @click="handleSave">Save</button>
      </div>
    </div>
  </dialog>
</template>
